'use client';

import { useEffect, useState } from 'react';
import { AdProvider } from './AdContext';
import AdLayout from './AdLayout';
import AdBlockerGate from './AdBlockerGate';
import { ADSENSE_SLOT_SIDE, ADSENSE_SLOT_BOTTOM } from './adsenseConfig';

export default function AdWrapper({ 
  children, 
  user = undefined,
  hasPaidServer = false,
  showSideAds = true,
  showLeftSideAd = false,
  showBottomAd = true,
}) {
  const [resolvedUser, setResolvedUser] = useState(user);
  const [resolvedHasPaidServer, setResolvedHasPaidServer] = useState(hasPaidServer);
  const userNeedsHydration = user === undefined || (
    !!user && (
      typeof user?.ads_disabled === 'undefined' ||
      typeof user?.show_ads_on_paid === 'undefined' ||
      typeof user?.has_paid_server === 'undefined'
    )
  );
  // Start hydrating=true when we need a fetch; prevents gray-box flicker while
  // we don't yet know if the user has ads enabled or disabled.
  const [hydrating, setHydrating] = useState(userNeedsHydration);

  useEffect(() => {
    setResolvedUser(user);
    setResolvedHasPaidServer(!!(hasPaidServer || user?.has_paid_server));
  }, [user, hasPaidServer]);

  useEffect(() => {
    if (!userNeedsHydration) {
      setHydrating(false);
      return;
    }

    let cancelled = false;
    fetch('/api/auth/me', { method: 'GET' })
      .then((res) => res.json().catch(() => null))
      .then((data) => {
        if (cancelled) return;
        const safeUser = data?.user ?? null;
        const mergedUser = safeUser ? { ...(user || {}), ...safeUser } : null;
        setResolvedUser(mergedUser);
        setResolvedHasPaidServer(!!(hasPaidServer || mergedUser?.has_paid_server));
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedUser(user ?? null);
        setResolvedHasPaidServer(!!(hasPaidServer || user?.has_paid_server));
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, hasPaidServer, userNeedsHydration]);

  return (
    <AdProvider user={resolvedUser} hasPaidServer={resolvedHasPaidServer} hydrating={hydrating}>
      <AdLayout 
        showSideAds={showSideAds} 
        showLeftSideAd={showLeftSideAd}
        showBottomAd={showBottomAd} 
        sideAdSlot={ADSENSE_SLOT_SIDE} 
        bottomAdSlot={ADSENSE_SLOT_BOTTOM}
      >
        <AdBlockerGate>{children}</AdBlockerGate>
      </AdLayout>
    </AdProvider>
  );
}

